/**
 * Order status helpers
 * ====================
 *
 * Decides which bucket an order falls into, and whether it is running late.
 * The date arithmetic itself lives in ./deliveryDate.js - edit that file if you
 * need to change how the promised date is worked out.
 */

import { getGulfToday, daysBetween, toISODate, parseStoredDate } from './deliveryDate'

/* =========================================================================
 * TUNABLE SETTINGS - safe to edit
 * ========================================================================= */

/**
 * Finished or abandoned orders. These are excluded from the "undelivered"
 * count in the table header.
 */
export const CLOSED_STATUSES = [
    "DELIVERED",
    "RETURNED",
    "CANCELLED",
    "RETURNED_TO_ORIGIN",
]

/**
 * Orders still waiting to be dispatched. These are the ONLY statuses that get
 * a ship-by message or can be flagged as delayed - once an order is CONFIRMED
 * or SHIPPED it is already moving, so chasing it here would be noise.
 *
 * "PAYMENTPENDING" is shown as "Payment Pending" in the status dropdown.
 */
export const PENDING_STATUSES = ["NEW", "PAYMENTPENDING"]

/**
 * Statuses that show live delivery tracking on the row: the "Ship by ..."
 * message and the live "Delayed Order" badge.
 *
 * Only NEW. A PAYMENTPENDING order is a subset of pending (see PENDING_STATUSES)
 * that has not been paid for yet, so we do not chase its delivery date - but it
 * is still snapshotted on confirm, which is why this is separate from
 * PENDING_STATUSES rather than the same list.
 */
export const LIVE_TRACKING_STATUSES = ["NEW"]

/* =========================================================================
 * Helpers
 * ========================================================================= */

const getStatus = (order) => order?.attributes?.status

export const isOrderClosed = (order) => CLOSED_STATUSES.includes(getStatus(order))

export const isPendingOrder = (order) => PENDING_STATUSES.includes(getStatus(order))

export const showsLiveTracking = (order) => LIVE_TRACKING_STATUSES.includes(getStatus(order))

/**
 * The date this order was promised to the customer, or null if the order has
 * no promised date stored.
 *
 * `expectedDeliveryDate` is frozen onto the order by the storefront at creation
 * time, and is authoritative: it is the promise the customer actually saw.
 * Unlike a recalculation it cannot shift later when a product's deliveryLatency
 * is edited or the delivery rules change.
 */
export const getExpectedDeliveryDate = (order) =>
    parseStoredDate(order?.attributes?.expectedDeliveryDate)

/**
 * True when a live-tracked order (NEW) is past its promised day. Drives the
 * live "Delayed Order" badge. The comparison is on whole days, so an order due
 * Tuesday only turns red on Wednesday - not at some arbitrary hour on Tuesday.
 */
export const isOrderDelayed = (order) => {
    if (!showsLiveTracking(order)) return false

    const due = getExpectedDeliveryDate(order)
    if (!due) return false

    return getGulfToday().getTime() > due.getTime()
}

/**
 * The status we snapshot the delay on. Once an order leaves the pending
 * statuses the delay is no longer derivable, so it has to be frozen here.
 */
export const SNAPSHOT_ON_STATUS = "CONFIRMED"

/**
 * Freezes how the order was tracking against its promised date at the moment
 * it is confirmed, so it can be reported on later.
 *
 * Note this records whether the order was late being *processed* - it was still
 * sitting unshipped past its promised date - not whether it was late being
 * physically delivered, which is not knowable at confirm time.
 *
 * Returns null when the order is not pending (nothing meaningful to record) or
 * when the order has no latency data to compare against.
 *
 * @returns {{wasDelayed: boolean, daysLate: number,
 *            expectedDeliveryDate: string, confirmedAt: string}|null}
 */
export const buildDelaySnapshot = (order) => {
    if (!isPendingOrder(order)) return null

    const due = getExpectedDeliveryDate(order)
    if (!due) return null

    // Clamped at 0: an order confirmed early is simply "not late".
    const daysLate = Math.max(0, daysBetween(due, getGulfToday()))

    return {
        wasDelayed: daysLate > 0,
        daysLate,
        expectedDeliveryDate: toISODate(due),
        confirmedAt: new Date().toISOString(),
    }
}

/**
 * Reads back the snapshot frozen onto the order at confirm time.
 *
 * Only confirmed (and later) orders carry one - while an order is still
 * pending the delay is derived live instead, via isOrderDelayed. Orders
 * confirmed before this feature shipped have no snapshot, so this returns null
 * and no tag is shown: we genuinely do not know whether they ran late.
 *
 * Accepts both storage shapes, since the Strapi controller may either nest the
 * snapshot in one JSON field or spread it into top-level columns.
 */
export const getSavedDelayInfo = (order) => {
    const attrs = order?.attributes
    if (!attrs) return null

    // Nested: a single JSON field holding the whole snapshot.
    const nested = attrs.deliveryInfo || attrs.deliveryDelay
    if (nested && typeof nested === "object" && typeof nested.wasDelayed === "boolean") {
        return nested
    }

    // Spread: the snapshot's keys saved as individual fields on the order.
    if (typeof attrs.wasDelayed === "boolean") {
        return {
            wasDelayed: attrs.wasDelayed,
            daysLate: attrs.daysLate,
            expectedDeliveryDate: attrs.expectedDeliveryDate,
            confirmedAt: attrs.confirmedAt,
        }
    }

    return null
}

/**
 * Statuses that never show the saved "Delayed" tag - the pending ones.
 *
 * A pending order has its delay worked out live by isOrderDelayed, so showing
 * the saved tag too would double up on the same row.
 *
 * This has to be checked explicitly rather than inferred from the snapshot
 * being absent: Strapi writes the snapshot on the move to CONFIRMED but never
 * clears it, so an order moved back to a pending status would otherwise still
 * carry - and display - one.
 *
 * Points at PENDING_STATUSES so the two cannot drift apart; if you ever need
 * the tag hidden on a non-pending status, list them out separately here.
 */
export const HIDE_SAVED_TAG_STATUSES = PENDING_STATUSES

/** True when the order was recorded as running late at the time it was confirmed. */
export const wasOrderDelayed = (order) => {
    if (HIDE_SAVED_TAG_STATUSES.includes(getStatus(order))) return false
    return getSavedDelayInfo(order)?.wasDelayed === true
}

// Re-exported so components can pull everything they need from this one file.
export { formatDeliveryDate } from './deliveryDate'
